import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { fetchPaymentStatus, fetchReceipt, formatPrice } from "@/lib/api";

export const Grazie = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(sessionId ? "pending" : "missing");
  const [receipt, setReceipt] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      while (!stop && tries.current < 12) {
        try {
          const s = await fetchPaymentStatus(sessionId);
          if (s.payment_status === "paid") {
            setStatus("paid");
            const r = await fetchReceipt(sessionId).catch(() => null);
            if (r) setReceipt(r);
            return;
          }
        } catch {
          setStatus("error");
          return;
        }
        tries.current += 1;
        await new Promise((res) => setTimeout(res, 2000));
      }
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        data-testid="order-success-container"
        className="w-full max-w-2xl rounded-3xl border border-lineblue bg-panel p-10 text-center sm:p-14"
      >
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-electric" />
            <h1 className="mt-8 font-display text-3xl font-bold text-white">
              Verifichiamo il pagamento…
            </h1>
            <p className="mt-4 text-sm text-slate-400">Un attimo, stiamo confermando con Stripe.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <CheckCircle2 className="mx-auto h-16 w-16 text-electric" />
            </motion.div>
            <h1 data-testid="order-success-title" className="mt-8 font-display text-3xl sm:text-4xl font-bold text-white">
              Ordine confermato.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Grazie! Un nostro produttore senior ti contatterà entro un
              giorno lavorativo per avviare il progetto.
            </p>

            {receipt && (
              <div data-testid="order-success-receipt" className="mt-10 rounded-2xl border border-lineblue bg-surface/60 p-6 text-left">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">
                  Ordine {receipt.session_id.slice(0, 18).toUpperCase()}
                </p>
                <ul className="mt-4 space-y-3">
                  {receipt.items.map((it, i) => (
                    <li key={i} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-slate-200">
                        {it.title} <span className="text-slate-500">× {it.quantity}</span>
                      </span>
                      <span className="font-display font-bold text-white">
                        {formatPrice(it.unit_amount * it.quantity / 100)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center justify-between border-t border-lineblue pt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Totale pagato</span>
                  <span data-testid="order-success-total" className="font-display text-xl font-bold text-electric">
                    {formatPrice(receipt.amount)}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/pacchetti"
                data-testid="order-success-continue"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-lineblue px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-electric hover:text-electric"
              >
                Continua a esplorare
              </Link>
            </div>
          </>
        )}

        {(status === "missing" || status === "error") && (
          <>
            <h1 className="font-display text-3xl font-bold text-white">
              Nessun ordine da mostrare
            </h1>
            <p className="mt-4 text-sm text-slate-400">
              Se hai appena completato un pagamento, torna al carrello e riprova.
            </p>
            <Link
              to="/pacchetti"
              data-testid="order-success-back-catalog"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-glow"
            >
              Vai al catalogo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        <p className="mt-10 flex items-center justify-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-600">
          <CalendarCheck className="h-3.5 w-3.5" /> Modalità test Stripe — nessun addebito reale
        </p>
      </motion.div>
    </div>
  );
};

export default Grazie;
